// SectionHeading.jsx
import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const SectionHeading = ({ SectionNumber, SectionTitle }) => {
    const containerRef = useRef(null);
    const numberRef = useRef(null);
    const titleRef = useRef(null);
    const observerRef = useRef(null);
    const ctxRef = useRef(null);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        ctxRef.current = gsap.context(() => {
            const num = numberRef.current;
            const title = titleRef.current;

            // Initial states
            gsap.set([num, title], { autoAlpha: 0, y: 50 });

            const animateHeading = () => {
                gsap.to([num, title], {
                    autoAlpha: 1,
                    y: 0,
                    duration: 1.2,
                    ease: "power3.out",
                    stagger: 0.3,
                });
            };

            // If ScrollTrigger is available
            if (typeof ScrollTrigger !== "undefined") {
                ScrollTrigger.create({
                    trigger: el,
                    start: "top 70%",
                    once: true,
                    onEnter: () => {
                        animateHeading();
                    }
                });

                setTimeout(() => {
                    ScrollTrigger.refresh();
                }, 300);
            } else {
                // IntersectionObserver fallback
                const io = new IntersectionObserver((entries, obs) => {
                    if (entries[0].isIntersecting) {
                        animateHeading();
                        obs.disconnect();
                    }
                });

                observerRef.current = io;
                io.observe(el);
            }

        }, containerRef);

        return () => {
            if (observerRef.current) observerRef.current.disconnect();
            if (ctxRef.current) ctxRef.current.revert();
        };
    }, []);

    return (
        <>
            {/* Prevent flash & purge issues */}
            <style>{`
                .section-heading-anim {
                    opacity: 0;
                    transform: translateY(50px);
                }
            `}</style>

            <div
                ref={containerRef}
                className="heading-content relative w-full flex items-center overflow-hidden"
            >
                <span
                    ref={numberRef}
                    className="section-heading-anim text-[120px] md:text-[180px] font-extrabold text-[#40404069]"
                >
                    {SectionNumber}
                </span>

                <h2
                    ref={titleRef}
                    className="section-heading-anim absolute left-0 text-4xl md:text-5xl font-semibold tracking-tight text-text-light"
                >
                    {SectionTitle}
                </h2>
            </div>
        </>
    );
};

export default SectionHeading;
